import React, { useEffect, useRef, useState } from 'react';
import { Command } from '../../helpers/commandAutocomplete';
import { CommandSuggestionsContainer, CommandSuggestionItem, CommandDescription, CommandFormat } from './styles';

interface CommandSuggestionsProps {
  commands: Command[];
  onSelect: (commandName: string) => void;
  onClose: () => void;
}

const CommandSuggestions: React.FC<CommandSuggestionsProps> = ({ commands, onSelect, onClose }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (target instanceof Element && !target.closest('.command-suggestions')) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : commands.length - 1)); // Loop to last
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev < commands.length - 1 ? prev + 1 : 0)); // Loop to first
      } else if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation(); // Prevent the event from bubbling up to the input field
        if (commands[selectedIndex]) {
          onSelect(commands[selectedIndex].name);
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [commands, onSelect, onClose, selectedIndex]);

  useEffect(() => {
    if (itemRefs.current[selectedIndex]) {
      itemRefs.current[selectedIndex]?.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex]);

  const formatCommandWithParameters = (command: Command): string => {
    let format = `/${command.name}`;

    if (command.arguments && command.arguments.length > 0) {
      command.arguments.forEach((arg) => {
        if (arg.required) {
          format += ` <${arg.placeholder || arg.name}>`;
        } else {
          format += ` [${arg.placeholder || arg.name}]`;
        }
      });
    }

    return format;
  };

  return (
    <CommandSuggestionsContainer className="command-suggestions" ref={containerRef}>
      {commands.map((command, index) => (
        <CommandSuggestionItem
          key={command.name}
          ref={(el) => (itemRefs.current[index] = el)}
          onClick={() => onSelect(command.name)}
          isActive={index === selectedIndex}
        >
          <CommandFormat>{formatCommandWithParameters(command)}</CommandFormat>
          <CommandDescription>{command.description}</CommandDescription>
        </CommandSuggestionItem>
      ))}
    </CommandSuggestionsContainer>
  );
};

export default CommandSuggestions;
